import React, { useState, useRef, useEffect } from "react";
import { registerProfessional } from "../../utils/professional-api";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import gbHero from "../../assets/gb-hero.jpg";
import gbShowcase1 from "../../assets/gb-showcase-1.jpg";
import gbShowcase2 from "../../assets/gb-showcase-2.jpg";
import gbShowcase3 from "../../assets/gb-showcase-3.jpg";
import "./solutions.css";
import "./green-building.css";

const GET_STARTED_URL = "https://web.conra.net";

const PROBLEMS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><circle cx="12" cy="17" r="0.6" fill="currentColor" />
            </svg>
        ),
        title: "Design intent drifts",
        desc: "Specified insulation gets swapped for a cheaper alternative, or orientation shifts to cut site-prep cost — quietly, with no record.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3 2 8l10 5 10-5-10-5Z" /><path d="M2 13l10 5 10-5" /><path d="M2 18l10 5 10-5" />
            </svg>
        ),
        title: "Deviations get buried",
        desc: "By the time an assessor reviews the building, site records exist in WhatsApp threads, scattered PDFs, and email chains that took weeks to collate.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
        ),
        title: "Real capital exposure",
        desc: "Green bonds, sustainability-linked loans, and IFC incentives hinge on a building performing as certified. If the record doesn't exist, neither does the claim.",
    },
];

const SHOWCASE_PROJECTS = [
    {
        badge: "EDGE ADVANCED",
        type: "Illustrative example · Mixed-Use",
        name: "Coastal Residences, Kenya",
        img: gbShowcase1,
        metrics: [
            { num: "44%", lbl: "Energy savings" },
            { num: "31%", lbl: "Water savings" },
        ],
    },
    {
        badge: "LEED GOLD",
        type: "Illustrative example · Office",
        name: "Business Park, Kenya",
        img: gbShowcase2,
        metrics: [
            { num: "38%", lbl: "Energy savings" },
            { num: "6/9", lbl: "Verified milestones" },
        ],
    },
    {
        badge: "EDGE CERTIFIED",
        type: "Illustrative example · Residential",
        name: "Lakeview Homes, Kenya",
        img: gbShowcase3,
        metrics: [
            { num: "29%", lbl: "Energy savings" },
            { num: "4", lbl: "Biophilic features" },
        ],
    },
];

const STANDARDS = ["EDGE", "LEED", "BREEAM", "KGBS", "WorldGBC", "IFC", "GBCI", "BRE"];

const HOW_IT_WORKS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" /><circle cx="12" cy="9" r="2.5" />
            </svg>
        ),
        title: "Geotagged milestone capture",
        desc: "Each milestone is captured on-site, tagged with GPS and a timestamp — checked against the design, not assumed from the drawing.",
        replaces: "Replaces: design-stage-only claims that are never checked again once construction starts.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M16 14.2c2.6.4 5 2.2 5 5.8" />
            </svg>
        ),
        title: "Two-party confirmation",
        desc: "Both the client and the contractor confirm a milestone before it's sealed — one party alone can't write the record.",
        replaces: "Replaces: self-certified reporting written and submitted by a single party.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
        ),
        title: "Tamper-evident sealing",
        desc: "Captures are signed at the moment they're taken, aligned to the C2PA standard — a later edit is detectable.",
        replaces: "Replaces: photos that can be re-dated, reused, or edited after submission.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2h9l5 5v15H6V2Z" /><path d="M15 2v5h5" /><path d="M9 13h6M9 17h6" />
            </svg>
        ),
        title: "One exportable record",
        desc: "A chronological, exportable trail ready for a certifier, lender, or regulator to review on demand.",
        replaces: "Replaces: evidence scattered across email threads and WhatsApp.",
    },
];

const OPEN_LAB_CARDS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 21V9l8-5 8 5v12" /><path d="M9 21v-6h6v6" />
            </svg>
        ),
        title: "Developers",
        desc: "See exactly what orientation, shading, or ventilation strategies were confirmed as built in a similar climate.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 6l4 4-9 9-4-4 9-9Z" /><path d="M17.5 2.5l4 4-2 2-4-4 2-2Z" /><path d="M3 21l3-3" />
            </svg>
        ),
        title: "Contractors",
        desc: "See how verified milestones were structured on comparable green builds before you're partway through your own.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 17 17 3l4 4L7 21l-4-4Z" /><path d="M13 7l2 2M9 11l2 2M6 14l2 2" />
            </svg>
        ),
        title: "Architects & Engineers",
        desc: "Study verified precedent — material substitutions, passive cooling, daylighting layouts that hit carbon targets.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 9l10-5 10 5-10 5-10-5Z" /><path d="M6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
            </svg>
        ),
        title: "Students & Researchers",
        desc: "Secure access to ground-level construction data for studying building cycles and sustainable materials.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3 2 8l10 5 10-5-10-5Z" /><path d="M2 13l10 5 10-5" /><path d="M2 18l10 5 10-5" />
            </svg>
        ),
        title: "Material Manufacturers",
        desc: "Point to specific verified projects that used your product on-site — a real, checkable reference instead of a spec-sheet claim.",
    },
];

const FINANCE_CARDS = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
        ),
        title: "For banks",
        desc: "Verified milestone data supports disbursement decisions on green construction lending, in place of self-reported progress alone.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 17l6-6 4 4 8-9" /><path d="M15 6h6v6" />
            </svg>
        ),
        title: "For institutional investors",
        desc: "Real-time, tamper-evident evidence feeds ESG reporting directly, alongside a fund's existing green asset reporting.",
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2h9l5 5v15H6V2Z" /><path d="M15 2v5h5" /><path d="M9 13h6M9 17h6" />
            </svg>
        ),
        title: "For green bond issuers",
        desc: "A ConRa-verified pipeline supports use-of-proceeds reporting with an on-site evidence trail alongside existing disclosures.",
    },
];

const ROLE_PLACEHOLDERS = {
    edge_auditor: "Your GBCI EDGE Auditor certificate ID (see your GBCI account)",
    edge_expert: "Your GBCI EDGE Expert Exam Eligibility ID",
    breeam_assessor: "RW16 — 2 initials + assessor number, from BRE",
    leed_green_rater: "Your GBCI credential ID (see your usgbc.org account)",
    commissioning_authority: "Your CxA / commissioning certification ID",
    kgbs_worldgbc_assessor: "Your KGBS or national council member ID",
    other_verifier: "Your certification body's registration number",
};

const TURNSTILE_SITEKEY = "0x4AAAAAAEL_0YJSgfZT-2nV";

const RegistryForm = () => {
    const [role, setRole] = useState("");
    const [country, setCountry] = useState("");
    const [name, setName] = useState("");
    const [regNo, setRegNo] = useState("");
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [turnstileToken, setTurnstileToken] = useState("");

    const turnstileContainerRef = useRef(null);
    const turnstileWidgetId = useRef(null);

    useEffect(() => {
        const callbackName = "__turnstileReadyGB";

        const mountWidget = () => {
            if (!turnstileContainerRef.current || turnstileWidgetId.current !== null) return;
            turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
                sitekey: TURNSTILE_SITEKEY,
                callback: (token) => setTurnstileToken(token),
                "expired-callback": () => setTurnstileToken(""),
                "error-callback": () => setTurnstileToken(""),
            });
        };

        window[callbackName] = mountWidget;

        const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
        if (existing) {
            if (window.turnstile) mountWidget();
            else existing.addEventListener("load", mountWidget, { once: true });
        } else {
            const script = document.createElement("script");
            script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${callbackName}&render=explicit`;
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }

        return () => {
            delete window[callbackName];
            if (turnstileWidgetId.current !== null && window.turnstile) {
                window.turnstile.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = null;
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await registerProfessional({
                name,
                email,
                registration_no: regNo,
                professional_type: role,
                area_of_operation: country,
                turnstile_token: turnstileToken,
            });
            setSubmitted(true);
        } catch (err) {
            if (turnstileWidgetId.current !== null && window.turnstile) {
                window.turnstile.reset(turnstileWidgetId.current);
            }
            setTurnstileToken("");
            setError(err.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const ready = !!(role && country && name.trim() && regNo.trim() && email.trim() && turnstileToken);

    return submitted ? (
        <div className="gb-form-success">
            <p>Your credential has been submitted for verification. We'll be in touch shortly.</p>
        </div>
    ) : (
        <form className="gb-form" onSubmit={handleSubmit}>
            <h3 className="gb-form-title">Link Your Credential</h3>
            <p className="gb-form-sub">Free for individual professionals. Takes about 2 minutes.</p>

            <div className="gb-field">
                <label htmlFor="gb-role">Registering as</label>
                <select id="gb-role" required value={role} onChange={(e) => setRole(e.target.value)}>
                    <option value="" disabled>Select your role…</option>
                    <option value="edge_auditor">EDGE Auditor</option>
                    <option value="edge_expert">EDGE Expert</option>
                    <option value="breeam_assessor">BREEAM Assessor</option>
                    <option value="leed_green_rater">LEED Green Rater</option>
                    <option value="commissioning_authority">Commissioning Authority (CxA)</option>
                    <option value="kgbs_worldgbc_assessor">KGBS / WorldGBC Certified Assessor</option>
                    <option value="other_verifier">Other certification body verifier</option>
                </select>
            </div>

            <div className="gb-field">
                <label htmlFor="gb-country">Country of registration</label>
                <select id="gb-country" required value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="" disabled>Select country…</option>
                    <option value="Kenya">🇰🇪 Kenya</option>
                    <option value="Uganda">🇺🇬 Uganda</option>
                    <option value="Tanzania">🇹🇿 Tanzania</option>
                    <option value="Rwanda">🇷🇼 Rwanda</option>
                    <option value="Ethiopia">🇪🇹 Ethiopia</option>
                    <option value="Nigeria">🇳🇬 Nigeria</option>
                    <option value="South Africa">🇿🇦 South Africa</option>
                    <option value="Egypt">🇪🇬 Egypt</option>
                    <option value="United States">🇺🇸 United States</option>
                    <option value="United Kingdom">🇬🇧 United Kingdom</option>
                    <option value="Germany">🇩🇪 Germany</option>
                    <option value="Netherlands">🇳🇱 Netherlands</option>
                    <option value="United Arab Emirates">🇦🇪 United Arab Emirates</option>
                    <option value="Singapore">🇸🇬 Singapore</option>
                    <option value="Australia">🇦🇺 Australia</option>
                    <option value="Other">🌐 Other</option>
                </select>
            </div>

            <div className="gb-field">
                <label htmlFor="gb-name">Full name</label>
                <input type="text" id="gb-name" placeholder="Full name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="gb-field">
                <label htmlFor="gb-reg-no">Registration / Credential ID</label>
                <input
                    type="text"
                    id="gb-reg-no"
                    placeholder={ROLE_PLACEHOLDERS[role] || "Select a role above for the right format"}
                    required
                    value={regNo}
                    onChange={(e) => setRegNo(e.target.value)}
                />
            </div>

            <div className="gb-field">
                <label htmlFor="gb-email">Email address</label>
                <input type="email" id="gb-email" placeholder="you@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div ref={turnstileContainerRef} />

            {error && <p className="gb-form-error">{error}</p>}
            <button type="submit" className="filled-button gb-form-submit" disabled={!ready || loading}>
                {loading ? "Submitting…" : "Verify & Register"}
            </button>
            <p className="gb-form-note">Protected by Cloudflare — this check keeps bots from overloading the registry.</p>
        </form>
    );
};

const GreenBuildingPage = () => (
    <div className="gb-page">
        <div className="web-only"><HeaderAny /></div>
        <div className="mobile-only"><MobileNav /></div>

        {/* ── Hero ── */}
        <section className="gb-hero">
            <div className="gb-wrap">
                <div className="gb-hero-grid">
                    <div className="gb-hero-text">
                        <span className="gb-kicker">Verified Green Building</span>
                        <h1 className="gb-hero-h1">
                            A certification works best when it reflects what was actually built.
                        </h1>
                        <p className="gb-lede">
                            A certificate confirms intent. ConRa confirms the milestones and what was actually built. It turns that into a tamper-evident, on-site record that certifiers, lenders, and ESG-conscious capital can check the claim against.
                        </p>
                        <div className="gb-hero-ctas">
                            <a className="filled-button" href="#showcase">Browse the showcase</a>
                            <button
                                className="sol-outlined-button"
                                onClick={() => document.getElementById("gb-pain")?.scrollIntoView({ behavior: "smooth" })}
                            >
                                See the trust gap
                            </button>
                        </div>
                    </div>
                    <div className="gb-hero-art">
                        <img src={gbHero} alt="Green building with living wall" className="gb-hero-art-img" />
                    </div>
                </div>
            </div>
        </section>

        {/* ── Pain ── */}
        <section className="gb-section gb-alt" id="gb-pain">
            <div className="gb-wrap">
                <div className="gb-pain-grid">
                    <div className="gb-section-head" style={{ marginBottom: 0 }}>
                        <span className="gb-kicker">The trust gap</span>
                        <h2 className="gb-section-h2">
                            Buildings account for roughly 37% of global energy-related emissions — most green claims are still just design promises.
                        </h2>
                        <p className="gb-cite">Source: UNEP / Global Alliance for Buildings and Construction, Global Status Report for Buildings and Construction.</p>
                    </div>
                    <div className="gb-donut-wrap">
                        <svg viewBox="0 0 160 160" className="gb-donut">
                            <circle cx="80" cy="80" r="66" fill="none" stroke="#e3e7ef" strokeWidth="14" />
                            <circle cx="80" cy="80" r="66" fill="none" stroke="#d0342c" strokeWidth="14" strokeLinecap="round" strokeDasharray="415" strokeDashoffset="107" transform="rotate(-90 80 80)" />
                            <text x="80" y="72" textAnchor="middle" fontSize="28" fontWeight="800" fill="var(--color-text-primary)" fontFamily="Inter,sans-serif">37%</text>
                            <text x="80" y="94" textAnchor="middle" fontSize="9.5" fill="#9ca3af" fontFamily="Inter,sans-serif">of global emissions</text>
                        </svg>
                    </div>
                </div>
                <div className="gb-grid3">
                    {PROBLEMS.map((p) => (
                        <div key={p.title} className="gb-card">
                            <div className="gb-card-icon">{p.icon}</div>
                            <h3>{p.title}</h3>
                            <p>{p.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Showcase ── */}
        <section className="gb-section" id="showcase">
            <div className="gb-wrap">
                <div className="gb-section-head">
                    <span className="gb-kicker">Project Showcase</span>
                    <h2 className="gb-section-h2">A searchable record of what green building actually looks like, built.</h2>
                    <p className="gb-section-sub">Illustrative examples — not actual projects or clients.</p>
                </div>
                <div className="gb-showcase-grid">
                    {SHOWCASE_PROJECTS.map((proj) => (
                        <div key={proj.name} className="gb-proj-card">
                            <div className="gb-proj-photo">
                                <img src={proj.img} alt={proj.name} className="gb-proj-photo-img" />
                                <div className="gb-proj-photo-overlay">
                                    <span className="gb-proj-badge">{proj.badge}</span>
                                    <div className="gb-proj-verified">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="gb-proj-body">
                                <div className="gb-proj-loc">{proj.type}</div>
                                <div className="gb-proj-name">{proj.name}</div>
                                <div className="gb-proj-metrics">
                                    {proj.metrics.map((m) => (
                                        <div key={m.lbl} className="gb-proj-metric">
                                            <div className="gb-metric-num">{m.num}</div>
                                            <div className="gb-metric-lbl">{m.lbl}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="gb-proj-link">
                                    <span>View verified record</span><span>→</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="gb-std-strip">
                    {STANDARDS.map((s) => (
                        <span key={s} className="gb-std-tag">{s}</span>
                    ))}
                </div>
            </div>
        </section>

        {/* ── How It Works ── */}
        <section className="gb-section gb-alt" id="how-it-works">
            <div className="gb-wrap">
                <div className="gb-section-head">
                    <span className="gb-kicker">How ConRa works</span>
                    <h2 className="gb-section-h2">Four mechanisms — and what each one replaces.</h2>
                    <p className="gb-section-sub">ConRa doesn't assess or certify a building. It produces the on-site record a certifier, lender, or auditor checks the claim against.</p>
                </div>
                <div className="gb-points">
                    {HOW_IT_WORKS.map((item) => (
                        <div key={item.title} className="gb-point">
                            <div className="gb-point-icon">{item.icon}</div>
                            <div>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                                <div className="gb-replaces">{item.replaces}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Registry ── */}
        <section className="gb-section" id="registry">
            <div className="gb-wrap">
                <div className="gb-section-head">
                    <span className="gb-kicker">Global Verification Registry</span>
                    <h2 className="gb-section-h2">Your credential says you're qualified to certify. Your ConRa profile proves what you actually verified.</h2>
                    <p className="gb-section-sub">GBCI, IFC, and BRE already confirm who holds an EDGE, LEED, or BREEAM verification credential. ConRa is the record of what that credential has actually done in the field.</p>
                </div>
                <span className="gb-example-flag">Illustrative example — not an actual professional or client project</span>
                <div className="gb-registry-panel">
                    <div className="gb-registry-profile">
                        <div className="gb-reg-head">
                            <div className="gb-reg-avatar">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
                                </svg>
                            </div>
                            <div>
                                <div className="gb-reg-name">John Doe</div>
                                <div className="gb-reg-sub">EDGE Auditor · GBCI Reg. EDGE-KE-2291</div>
                                <div className="gb-reg-note">Independent verifier — confirms a building meets its certification requirements</div>
                            </div>
                        </div>
                        <div className="gb-reg-stats">
                            <div className="gb-reg-stat"><div className="gb-stat-n">24</div><div className="gb-stat-l">Certified</div></div>
                            <div className="gb-reg-stat"><div className="gb-stat-n">7</div><div className="gb-stat-l">In Progress</div></div>
                        </div>
                        <div className="gb-reg-projects">
                            <div className="gb-reg-proj-row"><span>Coastal Residences, Kenya</span><span className="gb-code">KN21G08</span></div>
                            <div className="gb-reg-proj-row"><span>Business Park, Kenya</span><span className="gb-code">THRET09</span></div>
                            <div className="gb-reg-proj-row"><span>Lakeview Homes, Kenya</span><span className="gb-code">PILOT09</span></div>
                        </div>
                    </div>
                    <RegistryForm />
                </div>
            </div>
        </section>

        {/* ── Open Construction Lab ── */}
        <section className="gb-section gb-alt">
            <div className="gb-wrap">
                <div className="gb-section-head">
                    <span className="gb-kicker">The Open Construction Lab</span>
                    <h2 className="gb-section-h2">A shared reference, not a private archive.</h2>
                    <p className="gb-section-sub">Verified project data becomes a public record — the built environment as an open, secure learning lab.</p>
                </div>
                <div className="gb-grid4">
                    {OPEN_LAB_CARDS.map((card) => (
                        <div key={card.title} className="gb-card">
                            <div className="gb-card-icon">{card.icon}</div>
                            <h3>{card.title}</h3>
                            <p>{card.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Financing ── */}
        <section className="gb-section">
            <div className="gb-wrap">
                <div className="gb-section-head">
                    <span className="gb-kicker">Financing the green movement</span>
                    <h2 className="gb-section-h2">Verified data is what turns a green claim into a bankable one.</h2>
                    <p className="gb-section-sub">Third-party verified performance lowers perceived risk and is increasingly a factor in green bonds, sustainability-linked loans, and preferential lending terms.</p>
                </div>
                <div className="gb-grid3">
                    {FINANCE_CARDS.map((card) => (
                        <div key={card.title} className="gb-fin-card">
                            <div className="gb-fin-icon">{card.icon}</div>
                            <h3>{card.title}</h3>
                            <p>{card.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Closing CTA ── */}
        <section className="gb-closing" id="contact">
            <div className="gb-wrap gb-closing-inner">
                <h2 className="gb-closing-h2">Prove your green claim on one project first.</h2>
                <p className="gb-closing-sub">
                    A controlled pilot on a single certification-track project — verified evidence added to the case your ESG or sustainability claim already needs to make.
                </p>
                {/* <div className="gb-hero-ctas">
                    <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                        Request a pilot
                    </button>
                    <button className="sol-outlined-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                        Download the framework
                    </button>
                </div> */}
            </div>
        </section>

        <Footer />
    </div>
);

export default GreenBuildingPage;
